import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortableTable } from './hooks/useSortableTable';
import { navigateWithPreference } from './utils/navigationHelper';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    employeeNumber: true,
    name: true,
    jobTitle: true,
    department: true,
    phone: true,
    email: true,
    startDate: true,
    status: true,
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  useEffect(() => {
    fetchEmployees();
    loadColumnPreferences();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await apiFetch('/api/employees');
      const data = await response.json();
      setEmployees(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setLoading(false);
    }
  };

  const loadColumnPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/employees');
      const data = await response.json();
      if (data.preferences?.visibleColumns) {
        setVisibleColumns(data.preferences.visibleColumns);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const saveColumnPreferences = async (columns) => {
    try {
      await apiFetch('/api/preferences/default/employees', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: { visibleColumns: columns } })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const toggleColumn = (column) => {
    const updated = { ...visibleColumns, [column]: !visibleColumns[column] };
    setVisibleColumns(updated);
    saveColumnPreferences(updated);
  };

  // Get unique departments for filter
  const departments = ['all', ...new Set(employees.map(e => e.department).filter(Boolean))];

  // Filter employees
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = searchTerm === '' ||
      `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employee_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment = filterDepartment === 'all' || employee.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || employee.status === filterStatus;

    return matchesSearch && matchesDepartment && matchesStatus;
  });

  // Add sorting
  const { items: sortedEmployees, requestSort, getSortIndicator } = useSortableTable(filteredEmployees, { key: 'last_name', direction: 'asc' });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader
        title="Employees"
        subtitle={`${employees.length} total`}
        backTo="/settings/hr"
        backLabel="HR Management"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/employees/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Add Employee
          </button>
        }
      />

      <div className="px-4 py-3">
        {/* Filters */}
        <div className="mb-3 flex items-center gap-2">
            {/* Search */}
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />

            {/* Department Filter */}
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            >
              {departments.map(dept => (
                <option key={dept} value={dept}>
                  {dept === 'all' ? 'All Departments' : dept}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="On Leave">On Leave</option>
              <option value="Suspended">Suspended</option>
              <option value="Terminated">Terminated</option>
            </select>

            {/* Column Selector */}
            <div className="relative">
              <button
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                className="px-3 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg text-sm font-medium whitespace-nowrap"
              >
                ⚙️ Columns
              </button>

              {showColumnSelector && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <div className="p-3 space-y-2">
                    {Object.keys(visibleColumns).map(column => (
                      <label key={column} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={visibleColumns[column]}
                          onChange={() => toggleColumn(column)}
                          className="rounded text-slate-600"
                        />
                        <span className="text-sm capitalize">
                          {column.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        {/* Table */}
        <div className="bg-white rounded border border-gray-200">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative"><table className="w-full text-sm">
            <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
              <tr>
                {visibleColumns.employeeNumber && (
                  <th
                    className="text-left px-3 py-2 text-xs font-semibold text-gray-900 cursor-pointer hover:bg-gray-400 transition-colors select-none"
                    onClick={() => requestSort('employee_number')}
                  >
                    Employee #{getSortIndicator('employee_number')}
                  </th>
                )}
                {visibleColumns.name && (
                  <th
                    className="text-left px-3 py-2 text-xs font-semibold text-gray-900 cursor-pointer hover:bg-gray-400 transition-colors select-none"
                    onClick={() => requestSort('last_name')}
                  >
                    Name{getSortIndicator('last_name')}
                  </th>
                )}
                {visibleColumns.jobTitle && (
                  <th
                    className="text-left px-3 py-2 text-xs font-semibold text-gray-900 cursor-pointer hover:bg-gray-400 transition-colors select-none"
                    onClick={() => requestSort('job_title')}
                  >
                    Job Title{getSortIndicator('job_title')}
                  </th>
                )}
                {visibleColumns.department && (
                  <th
                    className="text-left px-3 py-2 text-xs font-semibold text-gray-900 cursor-pointer hover:bg-gray-400 transition-colors select-none"
                    onClick={() => requestSort('department')}
                  >
                    Department{getSortIndicator('department')}
                  </th>
                )}
                {visibleColumns.phone && (
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-900">Phone</th>
                )}
                {visibleColumns.email && (
                  <th
                    className="text-left px-3 py-2 text-xs font-semibold text-gray-900 cursor-pointer hover:bg-gray-400 transition-colors select-none"
                    onClick={() => requestSort('email')}
                  >
                    Email{getSortIndicator('email')}
                  </th>
                )}
                {visibleColumns.startDate && (
                  <th
                    className="text-left px-3 py-2 text-xs font-semibold text-gray-900 cursor-pointer hover:bg-gray-400 transition-colors select-none"
                    onClick={() => requestSort('start_date')}
                  >
                    Start Date{getSortIndicator('start_date')}
                  </th>
                )}
                {visibleColumns.status && (
                  <th
                    className="text-left px-3 py-2 text-xs font-semibold text-gray-900 cursor-pointer hover:bg-gray-400 transition-colors select-none"
                    onClick={() => requestSort('status')}
                  >
                    Status{getSortIndicator('status')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="20" className="px-6 py-12 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : sortedEmployees.length === 0 ? (
                <tr>
                  <td colSpan="20" className="px-6 py-12 text-center text-gray-500">
                    {searchTerm || filterDepartment !== 'all' || filterStatus !== 'Active'
                      ? 'No employees match your filters'
                      : 'No employees yet. Click "Add Employee" to get started.'}
                  </td>
                </tr>
              ) : (
                sortedEmployees.map((employee, index) => (
                  <tr
                    key={employee.id}
                    onClick={() => navigateWithPreference(navigate, `/employees/${employee.id}`, 'employees')}
                    className={`hover:bg-blue-200 cursor-pointer transition ${
                      index % 2 === 1 ? 'bg-blue-100' : 'bg-white'
                    }`}
                  >
                    {visibleColumns.employeeNumber && (
                      <td className="px-3 py-2 text-gray-900 font-medium">
                        {employee.employee_number || '-'}
                      </td>
                    )}
                    {visibleColumns.name && (
                      <td className="px-3 py-2 text-gray-900">
                        {employee.first_name} {employee.last_name}
                      </td>
                    )}
                    {visibleColumns.jobTitle && (
                      <td className="px-3 py-2 text-gray-700">{employee.job_title || '-'}</td>
                    )}
                    {visibleColumns.department && (
                      <td className="px-3 py-2 text-gray-700">{employee.department || '-'}</td>
                    )}
                    {visibleColumns.phone && (
                      <td className="px-3 py-2 text-gray-700">{employee.mobile || employee.phone || '-'}</td>
                    )}
                    {visibleColumns.email && (
                      <td className="px-3 py-2 text-gray-700">{employee.email || '-'}</td>
                    )}
                    {visibleColumns.startDate && (
                      <td className="px-3 py-2 text-gray-700">
                        {employee.start_date ? new Date(employee.start_date).toLocaleDateString('en-GB') : '-'}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          employee.status === 'Active' ? 'bg-green-100 text-green-800' :
                          employee.status === 'On Leave' ? 'bg-yellow-100 text-yellow-800' :
                          employee.status === 'Suspended' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {employee.status}
                        </span>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </div>
  );
}

export default Employees;
